class AudioMixingService {
	/**
	 * Mixes voice audio with background music
	 *
	 * @param voiceBlob - Voice audio blob
	 * @param musicUrl - URL to the background music
	 * @param musicStart - Start time of the music region (in seconds)
	 * @param musicEnd - End time of the music region (in seconds)
	 * @param voiceVolume - Voice volume (0-1)
	 * @param musicVolume - Music volume (0-1)
	 * @param outputDuration - Total duration of output audio (defaults to voice duration)
	 * @param voiceStartOffset - Offset in seconds where voice starts in output (for padding before voice)
	 * @param musicFadeIn - Offset in seconds where music fades in (for padding before music)
	 * @param musicFadeOut - Offset in seconds where music fades out (for padding after music)
	 * @returns Promise<Blob> - Mixed audio as blob
	 */
	async mixAudio(
		voiceBlob: Blob,
		musicUrl: string,
		musicStart: number,
		musicEnd: number,
		voiceVolume: number = 0.8,
		musicVolume: number = 0.4,
		outputDuration?: number,
		voiceStartOffset: number = 0
	): Promise<Blob> {
		const audioContext = new (window.AudioContext ||
			(window as any).webkitAudioContext)()

		try {
			const voiceArrayBuffer = await voiceBlob.arrayBuffer()
			const rawVoiceBuffer =
				await audioContext.decodeAudioData(voiceArrayBuffer)
			const voiceAudioBuffer = await this.compressVoiceBuffer(rawVoiceBuffer)

			const musicResponse = await fetch(musicUrl)
			const musicArrayBuffer = await musicResponse.arrayBuffer()
			const musicAudioBuffer =
				await audioContext.decodeAudioData(musicArrayBuffer)

			const voiceDuration = voiceAudioBuffer.duration
			const finalOutputDuration = outputDuration ?? voiceDuration
			const voiceStartSampleOffset = Math.floor(
				voiceStartOffset * audioContext.sampleRate
			)

			const outputLength = Math.floor(
				audioContext.sampleRate * finalOutputDuration
			)
			const numberOfChannels = voiceAudioBuffer.numberOfChannels
			const outputBuffer = audioContext.createBuffer(
				numberOfChannels,
				outputLength,
				audioContext.sampleRate
			)

			const outputChannels: Float32Array[] = []
			for (let i = 0; i < numberOfChannels; i++) {
				outputChannels.push(outputBuffer.getChannelData(i))
			}

			// Add voice audio with offset (for padding before voice)
			for (let ch = 0; ch < numberOfChannels; ch++) {
				const voiceData = voiceAudioBuffer.getChannelData(ch)
				const outputData = outputChannels[ch]

				for (let i = 0; i < voiceData.length; i++) {
					const outputIndex = voiceStartSampleOffset + i
					if (outputIndex < outputData.length) {
						outputData[outputIndex] += voiceData[i] * voiceVolume
					}
				}
			}

			const musicChannelCount = musicAudioBuffer.numberOfChannels
			const musicSampleStart = Math.floor(musicStart * audioContext.sampleRate)
			const musicSampleEnd = Math.floor(musicEnd * audioContext.sampleRate)
			const musicSamplesToUse = musicSampleEnd - musicSampleStart

			// Fade config
			const sampleRate = audioContext.sampleRate
			const fadeInDurationSec = voiceStartOffset
			const fadeOutDurationSec = 3
			const voiceEndTimeSec = voiceStartOffset + voiceDuration
			const fadeOutStartSec = voiceEndTimeSec
			const fadeOutEndSec = voiceEndTimeSec + fadeOutDurationSec

			// Sidechain: compute per-sample music gain reduction driven by voice amplitude
			const sidechainEnvelope = this.buildSidechainEnvelope(
				voiceAudioBuffer,
				outputLength,
				sampleRate,
				voiceStartOffset
			)

			for (let ch = 0; ch < numberOfChannels; ch++) {
				const musicData = musicAudioBuffer.getChannelData(
					Math.min(ch, musicChannelCount - 1)
				)
				const outputData = outputChannels[ch]

				let musicSampleIndex = 0
				for (let i = 0; i < outputData.length; i++) {
					const t = i / sampleRate // current time in seconds in the output

					// Base gain for music
					let gain = musicVolume

					// Fade-in: from 0 to voiceStart
					if (t < fadeInDurationSec) {
						gain *= t / fadeInDurationSec
					}

					// Fade-out: from voiceEndTime to voiceEndTime + 3s
					if (t >= fadeOutStartSec) {
						if (t >= fadeOutEndSec) {
							gain = 0
						} else {
							const fadeProgress = (t - fadeOutStartSec) / fadeOutDurationSec
							gain *= 1 - fadeProgress
						}
					}

					// If already fully faded out, no need to keep mixing music
					if (gain <= 0) {
						continue
					}

					// Apply sidechain ducking: reduce music gain when voice is active
					gain *= sidechainEnvelope[i]

					const sampleIndex =
						(musicSampleIndex % musicSamplesToUse) + musicSampleStart
					outputData[i] += musicData[sampleIndex] * gain

					musicSampleIndex++
				}
			}

			// Convert mixed AudioBuffer to a WAV blob (browser-native friendly)
			const audioBlob = await this.audioBufferToBlob(outputBuffer)
			return audioBlob
		} catch (error) {
			console.error('Error mixing audio:', error)
			throw error
		}
	}

	/**
	 * Applies broadcast-style dynamics compression to a voice buffer.
	 * Tames peaks and lifts quiet passages so the voice sits consistently
	 * in the mix and responds better to downstream normalization.
	 */
	private async compressVoiceBuffer(buffer: AudioBuffer): Promise<AudioBuffer> {
		const offlineCtx = new OfflineAudioContext(
			buffer.numberOfChannels,
			buffer.length,
			buffer.sampleRate
		)

		const source = offlineCtx.createBufferSource()
		source.buffer = buffer

		const compressor = offlineCtx.createDynamicsCompressor()
		compressor.threshold.value = -20 // dB — engage compression above -20 dB
		compressor.knee.value = 6 // dB — soft knee for a smooth onset
		compressor.ratio.value = 3 // 3:1 — gentle, transparent compression for voice
		compressor.attack.value = 0.003 // 3 ms — fast enough to catch consonant spikes
		compressor.release.value = 0.25 // 250 ms — avoids pumping between syllables

		source.connect(compressor)
		compressor.connect(offlineCtx.destination)
		source.start()

		return offlineCtx.startRendering()
	}

	/**
	 * Builds a per-sample gain reduction curve for the music track driven by
	 * the voice amplitude — i.e. a manual sidechain compressor / ducking effect.
	 *
	 * When the voice is loud the curve dips, reducing the music so the voice
	 * cuts through clearly. When the voice is silent the curve returns to 1.0
	 * (no reduction) so the music breathes back to full level.
	 *
	 * @param voiceBuffer      - Compressed voice AudioBuffer
	 * @param outputLength     - Total sample count of the mixed output
	 * @param sampleRate       - Sample rate of the output
	 * @param voiceStartOffset - Seconds before voice starts (padding)
	 * @param duckDepth        - Maximum fractional reduction at full voice level (0–1).
	 *                           0.63 ≈ −8.5 dB reduction at peak voice.
	 * @param attackSec        - How fast the duck engages when voice becomes active
	 * @param releaseSec       - How fast the music recovers during pauses
	 */
	private buildSidechainEnvelope(
		voiceBuffer: AudioBuffer,
		outputLength: number,
		sampleRate: number,
		voiceStartOffset: number,
		duckDepth: number = 0.63,
		attackSec: number = 0.006,
		releaseSec: number = 0.3
	): Float32Array {
		const envelope = new Float32Array(outputLength)

		// Pre-compute 1-pole IIR smoothing coefficients
		const attackCoeff = Math.exp(-1 / (attackSec * sampleRate))
		const releaseCoeff = Math.exp(-1 / (releaseSec * sampleRate))

		const voiceStartSample = Math.floor(voiceStartOffset * sampleRate)
		const voiceData = voiceBuffer.getChannelData(0) // mono envelope is sufficient

		let envFollower = 0

		for (let i = 0; i < outputLength; i++) {
			const voiceIndex = i - voiceStartSample
			const abs =
				voiceIndex >= 0 && voiceIndex < voiceData.length
					? Math.abs(voiceData[voiceIndex])
					: 0

			// Attack when signal rises, release when it falls
			if (abs > envFollower) {
				envFollower = attackCoeff * envFollower + (1 - attackCoeff) * abs
			} else {
				envFollower = releaseCoeff * envFollower + (1 - releaseCoeff) * abs
			}

			// gain = 1 at silence, (1 - duckDepth) at maximum voice level
			envelope[i] = 1 - duckDepth * envFollower
		}

		return envelope
	}

	private async audioBufferToBlob(audioBuffer: AudioBuffer): Promise<Blob> {
		const numberOfChannels = audioBuffer.numberOfChannels
		const sampleRate = audioBuffer.sampleRate
		const format = 1
		const bitDepth = 16

		const bytesPerSample = bitDepth / 8
		const frameLength = audioBuffer.length

		const numberOfFrames = frameLength
		const blockAlign = numberOfChannels * bytesPerSample
		const byteRate = sampleRate * blockAlign
		const dataSize = numberOfFrames * blockAlign

		const buffer = new ArrayBuffer(44 + dataSize)
		const view = new DataView(buffer)

		const writeString = (offset: number, string: string) => {
			for (let i = 0; i < string.length; i++) {
				view.setUint8(offset + i, string.charCodeAt(i))
			}
		}

		const floatTo16BitPCM = (
			output: DataView,
			offset: number,
			input: Float32Array
		) => {
			for (let i = 0; i < input.length; i++, offset += 2) {
				const s = Math.max(-1, Math.min(1, input[i]))
				output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
			}
		}

		writeString(0, 'RIFF')
		view.setUint32(4, 36 + dataSize, true)
		writeString(8, 'WAVE')
		writeString(12, 'fmt ')
		view.setUint32(16, 16, true)
		view.setUint16(20, format, true)
		view.setUint16(22, numberOfChannels, true)
		view.setUint32(24, sampleRate, true)
		view.setUint32(28, byteRate, true)
		view.setUint16(32, blockAlign, true)
		view.setUint16(34, bitDepth, true)
		writeString(36, 'data')
		view.setUint32(40, dataSize, true)

		const channels: Float32Array[] = []
		for (let i = 0; i < numberOfChannels; i++) {
			channels.push(audioBuffer.getChannelData(i))
		}

		let offset = 44
		const length = frameLength
		while (length > 0) {
			for (let i = 0; i < numberOfChannels; i++) {
				floatTo16BitPCM(view, offset, channels[i])
				offset += (frameLength * bytesPerSample) / numberOfChannels
			}
			break
		}

		return new Blob([buffer], { type: 'audio/wav' })
	}
}

export const audioMixingService = new AudioMixingService()
